import { FunctionComponent } from 'react';

import { Stack, Typography } from '@mui/material';

import { Avatar, Button, HStack } from '@dw/components/core';
import { useConfirm } from '@dw/hooks';
import { useAcceptDanceSchoolUserInviteMutation, useDeclineDanceSchoolUserInviteMutation } from '@dw/hooks/api/DanceSchoolUserApi';
import { getSchoolMarkKey } from '@dw/models/danceSchool';
import { IDanceSchoolUser } from '@dw/models/danceSchoolUser';

interface IPendingInviteListProps {
  invites: IDanceSchoolUser[];
  /** Accepted. The switcher stays put; the login screen switches into the school. */
  onAccepted?: (invite: IDanceSchoolUser) => void;
}

/**
 * The schools waiting for this person to say yes or no.
 *
 * One list for both places it is asked — the switcher, where it sits under the schools they are
 * already in, and the login screen, where it is the whole page because they are in none. They
 * differ by what happens after Accept, which is the caller's to decide, so a second copy would
 * only be two rows that drift apart the first time either is touched.
 *
 * The row is not itself clickable: joining a school is not something a stray click should do, so
 * the buttons are the only actions on it.
 */
export const PendingInviteList: FunctionComponent<IPendingInviteListProps> = (props: IPendingInviteListProps) => {

  const { invites, onAccepted } = props;

  const { confirm } = useConfirm();

  const {
    mutate: acceptInvite,
    isPending: acceptInviteLoading,
    variables: acceptInviteVariables
  } = useAcceptDanceSchoolUserInviteMutation({
    onSuccess: (response) => onAccepted?.(response)
  });

  const {
    mutate: declineInvite,
    isPending: declineInviteLoading,
    variables: declineInviteVariables
  } = useDeclineDanceSchoolUserInviteMutation();

  const isBusy = acceptInviteLoading || declineInviteLoading;

  /*
    Guarded rather than defaulted to ''. An empty id reaches the API as a DynamoDB key-validation
    failure - a 500, not the 400 a `?? ''` reads as - and the row cannot be addressed without it.
  */
  const handleAccept = (invite: IDanceSchoolUser) => {
    if (!invite.danceSchool?.id) {
      return;
    }

    acceptInvite({ danceSchoolId: invite.danceSchool.id });
  };

  const handleDecline = (invite: IDanceSchoolUser) => {
    if (!invite.danceSchool?.id) {
      return;
    }

    const danceSchoolId = invite.danceSchool.id;

    confirm({
      title: `Decline the invitation from ${invite.danceSchool?.name ?? 'this school'}?`,
      // Says what it costs and what it does not: declining is recoverable, which is what makes it
      // safe to offer beside Accept rather than hiding it behind a menu.
      message: 'They will not be told, and they can invite you again at any time.',
      positiveText: 'Decline',
      onPositive: () => declineInvite({ danceSchoolId })
    });
  };

  return (
    <Stack rowGap={0.5}>
      {invites.map(invite => (
        <HStack
          key={invite.danceSchool?.id}
          breakpoint={false}
          justifyContent='space-between'
          alignItems='center'
          className='school-option'
        >
          <HStack breakpoint={false} alignItems='center' columnGap={1}>
            <Avatar imageKey={getSchoolMarkKey(invite.danceSchool)} size='tiny' shape='square' />
            <Stack>
              <Typography variant='body2'>{invite.danceSchool?.name}</Typography>
              <Typography variant='caption'>Invited as {invite.role?.toLowerCase()}</Typography>
            </Stack>
          </HStack>

          <HStack breakpoint={false} alignItems='center' columnGap={1}>
            <Button
              variant='text'
              size='small'
              color='error'
              loading={declineInviteLoading && declineInviteVariables?.danceSchoolId === invite.danceSchool?.id}
              disabled={isBusy}
              onClick={() => handleDecline(invite)}
            >
              Decline
            </Button>

            <Button
              variant='contained'
              size='small'
              loading={acceptInviteLoading && acceptInviteVariables?.danceSchoolId === invite.danceSchool?.id}
              disabled={isBusy}
              onClick={() => handleAccept(invite)}
            >
              Accept
            </Button>
          </HStack>
        </HStack>
      ))}
    </Stack>
  );
};
